/**
 * Inventory Table Component
 *
 * The main data table for the Inventory Forecasting page. Displays:
 * - Inventory items grouped by inventory_id + country
 * - Period columns (day/week/month/quarter based on granularity)
 * - Metrics for each period: Starting, Sold, Added, Misc Adjustment, Ending
 *
 * Table Structure:
 * - Fixed columns: Inventory (name, ID, SKUs, manufacturer), Country, Metric
 * - Scrollable period columns with sticky headers
 * - Global modifiers row above column headers
 * - Per-inventory modifier row (expandable)
 *
 * Visual Features:
 * - Future periods highlighted in blue
 * - Negative inventory periods highlighted in red
 * - Alternating row colors for groupings
 * - Sticky first 3 columns for horizontal scrolling
 *
 * Performance Considerations:
 * - Pre-computed metric values via metricValuesByGroupingKey
 * - Memoized period columns
 * - Loading overlay during data fetches
 */

import React, { useState } from "react";
import moment from "moment";
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import AddIcon from "@mui/icons-material/Add";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import PushPinIcon from "@mui/icons-material/PushPin";
import PushPinOutlinedIcon from "@mui/icons-material/PushPinOutlined";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import UnfoldLessIcon from "@mui/icons-material/UnfoldLess";
import UnfoldMoreIcon from "@mui/icons-material/UnfoldMore";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { InventoryGrouping, IncomingPO, POContribution } from "models/InventoryModel";
import { BucketGranularity, PeriodColumn, PeriodMetrics, SalesModifiersData } from "../types";
import { formatNumber } from "../utils";
import { MOVE_POSITION, MovePosition } from "../productSort";
import { WHAT_IF_STATUS, WhatIfSimulation } from "../demandWhatIf";

interface InventoryTableProps {
  // Data
  pagedInventoryGroupings: InventoryGrouping[];
  periodColumns: PeriodColumn[];
  metricValuesByGroupingKey: Map<string, Map<string, PeriodMetrics>>;
  salesModifiers: SalesModifiersData;
  bucketGranularity: BucketGranularity;

  // Loading state
  isLoading: boolean;
  isLoadingHistory: boolean;
  errorMessage: string | null;

  // Callbacks for modifiers
  onUpdateGlobalModifier: (monthKey: string, value: number | null) => void;
  onUpdateInventoryModifier: (inventoryId: string, monthKey: string, value: number | null) => void;
  onClearInventoryModifiers: (inventoryId: string) => void;
  editingInventoryModifiers: string | null;
  onSetEditingInventoryModifiers: (key: string | null) => void;
  inventoryRunRateModifiers: Record<string, Record<string, number>>;

  // Callbacks for POs
  getFuturePOs: (inventoryId: string) => IncomingPO[];
  getConfirmedPOs: (inventoryId: string) => IncomingPO[];
  getDraftPOs: (inventoryId: string) => IncomingPO[];
  onOpenIncomingPODialog: (inventoryId: string) => void;
  onMarkReceived: (poNumbers: string[]) => void;
  onPromotePO: (poNumber: string) => void;

  // SKU modal
  onOpenSkuModal: (skus: string[], inventoryName: string) => void;

  // Sales projection detail dialog
  onOpenProjectionDetail: (inventoryGrouping: InventoryGrouping) => void;

  // Row ref registration for scroll-to-row functionality
  onRegisterRowRef?: (groupingKey: string, element: HTMLElement | null) => void;

  // Pinning and hand ordering of products
  productRowControls: ProductRowControls;

  // Unsaved demand what-ifs
  whatIfControls: WhatIfControls;

  // Product families (a single item and its multi-packs)
  familyControls: FamilyControls;
}

export interface FamilyControls {
  collapsedFamilyKeys: Set<string>;
  onToggleCollapsed: (familyKey: string) => void;
  /** Orders that shipped a different pack than usual, by family row key */
  substitutionsByFamilyKey: Map<string, { orders: number; giveawayUnits: number }>;
  substitutionDays: number;
}

export interface WhatIfControls {
  /** By grouping key: the rows currently showing what-if numbers instead of the saved forecast */
  simulationsByKey: Record<string, WhatIfSimulation>;
  /** Products (inventory ids) with unsaved adjustments: the ones Save applies to */
  unsavedInventoryIds: Set<string>;
  onDiscard: (inventoryId: string) => void;
  onSave: (inventoryId: string) => void;
}

/** The neighbours the up/down arrows move a product past (across pages; null at either end) */
export type ProductNeighbors = { previousKey: string | null; nextKey: string | null };

export interface ProductRowControls {
  pinnedKeys: Set<string>;
  neighborsByKey: Map<string, ProductNeighbors>;
  onTogglePin: (groupingKey: string) => void;
  /** Pinned products reorder among themselves, the rest among themselves */
  onMoveProduct: (movedKey: string, targetKey: string, position: MovePosition) => void;
  /** Rows shrunk to one line that shows only each period's ending inventory (any row, SKU or family) */
  collapsedRowKeys: Set<string>;
  onToggleRowCollapsed: (groupingKey: string) => void;
}

type DropTarget = { key: string; position: MovePosition };

const PINNED_ROW_BACKGROUND = "rgb(255, 251, 235)";
const WHAT_IF_ROW_BACKGROUND = "rgb(247, 242, 255)";
const FAMILY_ROW_BACKGROUND = "rgb(236, 247, 243)";
const FAMILY_COLOR = "#00695c";
// A pack running out this much sooner than its sibling is worth a warning
const PACK_IMBALANCE_DAYS = 21;
const WHAT_IF_COLOR = "#6a1b9a";

const formatForecastDate = (date: string | null | undefined): string => (date ? moment(date).format("MMM D, YYYY") : "none in range");
const HEADER_ROW_HEIGHT = 37; // px, matches GlobalModifiersRow compact height

export default function InventoryTable({
  pagedInventoryGroupings,
  periodColumns,
  metricValuesByGroupingKey,
  salesModifiers,
  bucketGranularity,
  isLoading,
  isLoadingHistory,
  errorMessage,
  onUpdateGlobalModifier,
  onUpdateInventoryModifier,
  onClearInventoryModifiers,
  editingInventoryModifiers,
  onSetEditingInventoryModifiers,
  inventoryRunRateModifiers,
  getFuturePOs,
  getConfirmedPOs,
  getDraftPOs,
  onOpenIncomingPODialog,
  onMarkReceived,
  onPromotePO,
  onOpenSkuModal,
  onOpenProjectionDetail,
  onRegisterRowRef,
  productRowControls,
  whatIfControls,
  familyControls,
}: InventoryTableProps): JSX.Element {
  const isTableLoading = isLoading || isLoadingHistory;
  const [draggedKey, setDraggedKey] = useState<string | null>(null);
  const [dropTarget, setDropTarget] = useState<DropTarget | null>(null);
  const loadingMessage = isLoading ? "Loading inventory..." : "Loading inventory history...";

  return (
    <Box sx={{ width: "100%", minWidth: 0, position: "relative", minHeight: 200 }}>
      {/* Loading Spinner - positioned relative to outer container, always centered */}
      {isTableLoading && (
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 11,
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "center",
            paddingTop: 8,
            pointerEvents: "none",
          }}
        >
          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 1 }}>
            <CircularProgress size={40} />
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              {loadingMessage}
            </Typography>
          </Box>
        </Box>
      )}

      {/* Error Message */}
      {errorMessage && (
        <Box sx={{ p: 2, mb: 2 }}>
          <Typography color="error">{errorMessage}</Typography>
        </Box>
      )}

      {/* Scrollable Table Container */}
      <Box sx={{ width: "100%", overflowX: "auto", overflowY: "auto", maxHeight: "75vh" }}>
        {/* Inline-block wrapper to size overlay to table width */}
        <Box sx={{ display: "inline-block", position: "relative", minWidth: "100%" }}>
          {/* Loading Overlay Background - covers full table width */}
          {isTableLoading && (
            <Box
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: "rgba(255, 255, 255, 0.7)",
                zIndex: 10,
                pointerEvents: "none",
              }}
            />
          )}

          <TableContainer
            component={Paper}
            sx={{
              width: "100%",
              overflowX: "visible",
            }}
          >
        <Table
          size="small"
          stickyHeader
          sx={{
            width: "max-content",
            minWidth: "100%",
          }}
        >
          <TableHead>
            {/* Global Modifiers Row */}
            <GlobalModifiersRow
              periodColumns={periodColumns}
              salesModifiers={salesModifiers}
              bucketGranularity={bucketGranularity}
              onUpdateGlobalModifier={onUpdateGlobalModifier}
            />

            {/* Column Headers Row */}
            <ColumnHeadersRow
              periodColumns={periodColumns}
              bucketGranularity={bucketGranularity}
              pagedInventoryGroupings={pagedInventoryGroupings}
              metricValuesByGroupingKey={metricValuesByGroupingKey}
            />
          </TableHead>

          <TableBody>
            {pagedInventoryGroupings.map((inventoryGrouping, groupingIndex) => (
              <InventoryGroupingRows
                key={inventoryGrouping.key}
                inventoryGrouping={inventoryGrouping}
                groupingIndex={groupingIndex}
                isLastGrouping={groupingIndex === pagedInventoryGroupings.length - 1}
                periodColumns={periodColumns}
                metricValuesByGroupingKey={metricValuesByGroupingKey}
                salesModifiers={salesModifiers}
                bucketGranularity={bucketGranularity}
                editingInventoryModifiers={editingInventoryModifiers}
                onSetEditingInventoryModifiers={onSetEditingInventoryModifiers}
                onUpdateInventoryModifier={onUpdateInventoryModifier}
                onClearInventoryModifiers={onClearInventoryModifiers}
                inventoryRunRateModifiers={inventoryRunRateModifiers}
                getFuturePOs={getFuturePOs}
                getConfirmedPOs={getConfirmedPOs}
                getDraftPOs={getDraftPOs}
                onOpenIncomingPODialog={onOpenIncomingPODialog}
                onMarkReceived={onMarkReceived}
                onPromotePO={onPromotePO}
                onOpenSkuModal={onOpenSkuModal}
                onOpenProjectionDetail={onOpenProjectionDetail}
                onRegisterRowRef={onRegisterRowRef}
                productRowControls={productRowControls}
                whatIfControls={whatIfControls}
                familyControls={familyControls}
                draggedKey={draggedKey}
                onDraggedKeyChange={setDraggedKey}
                dropTarget={dropTarget}
                onDropTargetChange={setDropTarget}
              />
            ))}

            {/* Empty State */}
            {pagedInventoryGroupings.length === 0 && (
              <TableRow>
                <TableCell
                  colSpan={3 + periodColumns.length}
                  sx={{
                    position: "sticky",
                    left: 0,
                    backgroundColor: "background.paper",
                    zIndex: 2,
                    minWidth: 520,
                  }}
                >
                  <Typography variant="body2" sx={{ opacity: 0.75 }}>
                    No rows match the current filter.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
        </Box>
      </Box>
    </Box>
  );
}

// ============================================================================
// Sub-components
// ============================================================================

/**
 * Global Modifiers Row - Input fields above column headers for global sales multipliers
 */
function GlobalModifiersRow({
  periodColumns,
  salesModifiers,
  bucketGranularity,
  onUpdateGlobalModifier,
}: {
  periodColumns: PeriodColumn[];
  salesModifiers: SalesModifiersData;
  bucketGranularity: BucketGranularity;
  onUpdateGlobalModifier: (monthKey: string, value: number | null) => void;
}): JSX.Element {
  const shownMonths = new Set<string>();
  const today = moment().format("YYYY-MM-DD");

  return (
    <TableRow sx={{ backgroundColor: "rgb(250, 250, 255)" }}>
      <TableCell
        colSpan={3}
        sx={{
          position: "sticky",
          left: 0,
          top: 0,
          backgroundColor: "rgb(250, 250, 255)",
          zIndex: 3,
          boxShadow: "2px 0 4px rgb(200, 200, 200)",
          py: 0.5,
        }}
      >
        <Typography variant="caption" sx={{ fontWeight: 500, opacity: 0.7 }}>
          Global Sales Multiplier
        </Typography>
      </TableCell>
      {periodColumns.map((periodColumn, colIndex) => {
        const hasFutureDates = periodColumn.periodEnd >= today;
        const monthKey = moment(periodColumn.periodStart).format("YYYY-MM");

        if (!hasFutureDates) {
          return (
            <TableCell
              key={periodColumn.key}
              sx={{ position: "sticky", top: 0, backgroundColor: "rgb(250, 250, 255)", py: 0.5 }}
            />
          );
        }

        const alreadyShown = shownMonths.has(monthKey);
        shownMonths.add(monthKey);

        let colspan = 1;
        if (!alreadyShown) {
          for (let i = colIndex + 1; i < periodColumns.length; i++) {
            const nextMonth = moment(periodColumns[i].periodStart).format("YYYY-MM");
            if (nextMonth === monthKey) {
              colspan++;
            } else {
              break;
            }
          }
        }

        if (alreadyShown) {
          return null;
        }

        const inputWidth = bucketGranularity === "day" ? 60 :
                           bucketGranularity === "week" ? 70 : 80;

        return (
          <TableCell
            key={periodColumn.key}
            colSpan={colspan}
            align="center"
            sx={{
              position: "sticky",
              top: 0,
              backgroundColor: "rgb(250, 250, 255)",
              py: 0.5,
              px: 0.5,
            }}
          >
            <Tooltip title={`Multiplier for ${moment(monthKey).format("MMM YYYY")} (1.0 = no change)`}>
              <TextField
                size="small"
                type="number"
                value={salesModifiers.global[monthKey] ?? ""}
                placeholder="1.0"
                onChange={(e) => {
                  const value = e.target.value === "" ? null : parseFloat(e.target.value);
                  onUpdateGlobalModifier(monthKey, value);
                }}
                inputProps={{
                  step: 0.1,
                  min: 0,
                  style: {
                    textAlign: "center",
                    padding: "4px 4px",
                    fontSize: "0.75rem",
                  },
                }}
                sx={{
                  width: inputWidth,
                  "& .MuiOutlinedInput-root": {
                    backgroundColor: (salesModifiers.global[monthKey] !== undefined && salesModifiers.global[monthKey] !== 1) ? "rgba(173, 216, 230, 0.2)" : "white"
                  },
                }}
              />
            </Tooltip>
          </TableCell>
        );
      }).filter(Boolean)}
    </TableRow>
  );
}

/**
 * Column Headers Row - Period column labels with date ranges
 */
function ColumnHeadersRow({
  periodColumns,
  bucketGranularity,
  pagedInventoryGroupings,
  metricValuesByGroupingKey,
}: {
  periodColumns: PeriodColumn[];
  bucketGranularity: BucketGranularity;
  pagedInventoryGroupings: InventoryGrouping[];
  metricValuesByGroupingKey: Map<string, Map<string, PeriodMetrics>>;
}): JSX.Element {
  const today = moment().format("YYYY-MM-DD");

  return (
    <TableRow>
      {/* Fixed columns */}
      <TableCell
        sx={{
          position: "sticky",
          left: 0,
          top: HEADER_ROW_HEIGHT,
          backgroundColor: "background.paper",
          zIndex: 3,
          width: 220,
          minWidth: 220,
          maxWidth: 220,
        }}
      >
        Inventory
      </TableCell>
      <TableCell
        sx={{
          position: "sticky",
          left: 220,
          top: HEADER_ROW_HEIGHT,
          backgroundColor: "background.paper",
          zIndex: 3,
          width: 100,
          minWidth: 100,
          maxWidth: 100,
        }}
      >
        Country
      </TableCell>
      <TableCell
        sx={{
          position: "sticky",
          left: 320,
          top: HEADER_ROW_HEIGHT,
          backgroundColor: "background.paper",
          zIndex: 3,
          width: 220,
          minWidth: 220,
          maxWidth: 220,
          boxShadow: "2px 0 4px rgb(200, 200, 200)",
        }}
      >
        Metric
      </TableCell>

      {/* Period columns */}
      {periodColumns.map((periodColumn) => {
        const isFuture = periodColumn.periodStart > today;

        const hasNegativeInventory = pagedInventoryGroupings.some((inventoryGrouping) => {
          const valuesForGrouping = metricValuesByGroupingKey.get(inventoryGrouping.key);
          const periodMetrics = valuesForGrouping?.get(periodColumn.key);
          const endingInventory = periodMetrics?.endingInventory ?? null;
          return endingInventory != null && endingInventory < 0;
        });

        let backgroundColor = undefined;
        if (isFuture) {
          backgroundColor = "rgb(240, 248, 255)";
        }
        if (hasNegativeInventory) {
          backgroundColor = "rgb(255, 240, 240)";
        }

        let humanReadableDate = periodColumn.key;
        if (bucketGranularity === "day") {
          humanReadableDate = moment(periodColumn.periodStart).format("MMM D");
        } else if (bucketGranularity === "week") {
          humanReadableDate = `Week ${moment(periodColumn.periodStart).format("W")}`;
        } else if (bucketGranularity === "month") {
          humanReadableDate = moment(periodColumn.periodStart).format("MMM YYYY");
        } else if (bucketGranularity === "quarter") {
          humanReadableDate = moment(periodColumn.periodStart).format("[Q]Q YYYY");
        }

        const dateRange = periodColumn.periodStart === periodColumn.periodEnd
          ? periodColumn.periodStart
          : `${periodColumn.periodStart} → ${periodColumn.periodEnd}`;

        return (
          <TableCell
            key={periodColumn.key}
            align="center"
            sx={{
              position: "sticky",
              top: HEADER_ROW_HEIGHT,
              backgroundColor: backgroundColor || "background.paper",
              minWidth: 120,
              whiteSpace: "normal",
              zIndex: 1,
            }}
          >
            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
              <Box sx={{ fontWeight: 500, lineHeight: 1.2 }}>
                {humanReadableDate}
              </Box>
              <Box sx={{ fontSize: "0.75em", opacity: 0.7, lineHeight: 1.2 }}>
                {dateRange}
              </Box>
            </Box>
          </TableCell>
        );
      })}
    </TableRow>
  );
}

/**
 * Inventory Grouping Rows - Renders all rows for a single inventory grouping
 */
function InventoryGroupingRows({
  inventoryGrouping,
  groupingIndex,
  isLastGrouping,
  periodColumns,
  metricValuesByGroupingKey,
  salesModifiers,
  bucketGranularity,
  editingInventoryModifiers,
  onSetEditingInventoryModifiers,
  onUpdateInventoryModifier,
  onClearInventoryModifiers,
  inventoryRunRateModifiers,
  getFuturePOs,
  getConfirmedPOs,
  getDraftPOs,
  onOpenIncomingPODialog,
  onMarkReceived,
  onPromotePO,
  onOpenSkuModal,
  onOpenProjectionDetail,
  onRegisterRowRef,
  productRowControls,
  whatIfControls,
  familyControls,
  draggedKey,
  onDraggedKeyChange,
  dropTarget,
  onDropTargetChange,
}: {
  inventoryGrouping: InventoryGrouping;
  groupingIndex: number;
  isLastGrouping: boolean;
  periodColumns: PeriodColumn[];
  metricValuesByGroupingKey: Map<string, Map<string, PeriodMetrics>>;
  salesModifiers: SalesModifiersData;
  bucketGranularity: BucketGranularity;
  editingInventoryModifiers: string | null;
  onSetEditingInventoryModifiers: (key: string | null) => void;
  onUpdateInventoryModifier: (inventoryId: string, monthKey: string, value: number | null) => void;
  onClearInventoryModifiers: (inventoryId: string) => void;
  inventoryRunRateModifiers: Record<string, Record<string, number>>;
  getFuturePOs: (inventoryId: string) => IncomingPO[];
  getConfirmedPOs: (inventoryId: string) => IncomingPO[];
  getDraftPOs: (inventoryId: string) => IncomingPO[];
  onOpenIncomingPODialog: (inventoryId: string) => void;
  onMarkReceived: (poNumbers: string[]) => void;
  onPromotePO: (poNumber: string) => void;
  onOpenSkuModal: (skus: string[], inventoryName: string) => void;
  onOpenProjectionDetail: (inventoryGrouping: InventoryGrouping) => void;
  onRegisterRowRef?: (groupingKey: string, element: HTMLElement | null) => void;
  productRowControls: ProductRowControls;
  whatIfControls: WhatIfControls;
  familyControls: FamilyControls;
  draggedKey: string | null;
  onDraggedKeyChange: (key: string | null) => void;
  dropTarget: DropTarget | null;
  onDropTargetChange: (target: DropTarget | null) => void;
}): JSX.Element {
  const valuesForGrouping = metricValuesByGroupingKey.get(inventoryGrouping.key) ?? new Map();

  // Extract current stock and inventory value (populated in the period containing today)
  let currentStock: number | null = null;
  let inventoryValue: number | null = null;
  for (const metrics of valuesForGrouping.values()) {
    if (metrics.currentStock != null) { currentStock = metrics.currentStock; }
    if (metrics.inventoryValue != null) { inventoryValue = metrics.inventoryValue; }
  }

  const metricDefinitions = [
    { key: "startingInventory", label: "Starting Inventory" },
    { key: "soldQuantity", label: "Sold / Projected Sales" },
    { key: "confirmedInventoryAdded", label: "Placed / Incoming" },
    { key: "draftInventoryAdded", label: "Draft / Proposed" },
    { key: "miscAdjustment", label: "Misc Adjustment" },
    { key: "endingInventory", label: "Ending Inventory" },
  ] as const;

  const today = moment().format("YYYY-MM-DD");
  const confirmedPOs = getConfirmedPOs(inventoryGrouping.inventory_id);
  const draftPOs = getDraftPOs(inventoryGrouping.inventory_id);
  const pastDueUnreceivedPOs = confirmedPOs.filter(
    (po) => po.arriveByDate <= today && po.received !== true
  );

  const { pinnedKeys, neighborsByKey, onTogglePin, onMoveProduct } = productRowControls;
  const isPinned = pinnedKeys.has(inventoryGrouping.key);
  const neighbors = neighborsByKey.get(inventoryGrouping.key);

  const isEvenGrouping = groupingIndex % 2 === 0;
  const whatIf = whatIfControls.simulationsByKey[inventoryGrouping.key];
  const family = inventoryGrouping.family;
  const isFamilyMember = inventoryGrouping.familyKey != null;
  const rowBackgroundColor = whatIf ? WHAT_IF_ROW_BACKGROUND
    : family ? FAMILY_ROW_BACKGROUND
    : isPinned ? PINNED_ROW_BACKGROUND : isEvenGrouping ? "background.paper" : "rgb(245, 245, 245)";
  // A family has no POs of its own and no projection basis of its own to open
  const canOpenProjectionDetail = !family;
  const isFamilyCollapsed = family ? familyControls.collapsedFamilyKeys.has(inventoryGrouping.key) : false;
  const familySubstitutions = family ? familyControls.substitutionsByFamilyKey.get(inventoryGrouping.key) : undefined;

  // A product can be dropped on another of its own kind: pinned among pinned, the rest among the rest
  const acceptsDrop = draggedKey != null && draggedKey !== inventoryGrouping.key && pinnedKeys.has(draggedKey) === isPinned;
  const dropPosition = dropTarget?.key === inventoryGrouping.key ? dropTarget.position : null;

  const hasInventorySpecificModifiers =
    salesModifiers.byInventory[inventoryGrouping.inventory_id] ||
    inventoryRunRateModifiers[inventoryGrouping.inventory_id];

  // Shrunk to one line: the name, and each period's ending inventory (same colours and hover as
  // the full row's Ending Inventory line). Only this row: a family's SKUs keep their own state.
  if (productRowControls.collapsedRowKeys.has(inventoryGrouping.key)) {
    const compactBorder = "2px solid rgba(0,0,0,0.12)";
    return (
      <TableRow
        ref={(el: HTMLTableRowElement | null) => onRegisterRowRef?.(inventoryGrouping.key, el)}
        sx={{ backgroundColor: rowBackgroundColor }}
      >
        <TableCell sx={{ width: 220, minWidth: 220, maxWidth: 220, position: "sticky", left: 0, zIndex: 2, backgroundColor: rowBackgroundColor, py: 0.5, borderBottom: compactBorder }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <Tooltip title="Show all of this row's numbers again" arrow>
              <IconButton size="small" sx={{ p: 0.25 }} onClick={() => productRowControls.onToggleRowCollapsed(inventoryGrouping.key)}>
                <UnfoldMoreIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
            <Box sx={{ minWidth: 0 }}>
              {family && <Box sx={{ fontWeight: 700, color: FAMILY_COLOR, fontSize: "0.65em", letterSpacing: 0.3 }}>ALL PACKS · {family.members.length} PRODUCTS</Box>}
              {isFamilyMember && <Box sx={{ fontSize: "0.65em", color: FAMILY_COLOR, fontWeight: 600 }}>↳ in family</Box>}
              <Box sx={{ fontSize: "0.8em", fontWeight: 600, lineHeight: 1.25 }}>{inventoryGrouping.inventory_name || inventoryGrouping.inventory_id}</Box>
              {!family && <Box sx={{ fontSize: "0.7em", opacity: 0.6 }}>{inventoryGrouping.inventory_id}</Box>}
            </Box>
          </Box>
        </TableCell>
        <TableCell sx={{ py: 0.5, fontSize: "0.8em", borderBottom: compactBorder }}>{inventoryGrouping.country}</TableCell>
        <TableCell sx={{ py: 0.5, fontWeight: 600, fontSize: "0.8em", borderBottom: compactBorder }}>Ending Inventory</TableCell>
        {periodColumns.map((periodColumn) => {
          const periodMetrics = valuesForGrouping.get(periodColumn.key);
          const endingInventory = periodMetrics?.endingInventory ?? null;
          const backgroundColor = endingInventory != null && endingInventory < 0 ? "rgb(255, 240, 240)"
            : periodColumn.periodStart > today ? "rgb(240, 248, 255)" : rowBackgroundColor;
          return (
            <TableCell
              key={`${inventoryGrouping.key}::compact::${periodColumn.key}`}
              align="right"
              sx={{ py: 0.5, fontWeight: 600, backgroundColor, borderBottom: compactBorder }}
            >
              {periodMetrics?.endingBreakdown ? (
                <Tooltip title={periodMetrics.endingBreakdown} arrow placement="top">
                  <Box component="span" sx={{ borderBottom: "1px dotted rgba(0,0,0,0.3)", cursor: "help" }}>
                    {formatNumber(endingInventory)}
                  </Box>
                </Tooltip>
              ) : formatNumber(endingInventory)}
            </TableCell>
          );
        })}
      </TableRow>
    );
  }

  const collapseRowButton = (
    <Tooltip title="Shrink this row to one line: just the ending inventory for each period" arrow>
      <IconButton size="small" sx={{ p: 0.25 }} onClick={() => productRowControls.onToggleRowCollapsed(inventoryGrouping.key)}>
        <UnfoldLessIcon sx={{ fontSize: 18 }} />
      </IconButton>
    </Tooltip>
  );

  const rows: JSX.Element[] = [];

  // Metric rows
  metricDefinitions.forEach((metricDefinition, metricIndex) => {
    rows.push(
      <TableRow
        key={`${inventoryGrouping.key}::${metricDefinition.key}`}
        ref={metricIndex === 0 ? (el: HTMLTableRowElement | null) => onRegisterRowRef?.(inventoryGrouping.key, el) : undefined}
        sx={{ backgroundColor: rowBackgroundColor }}
      >
        {/* First metric row includes the inventory and country cells */}
        {metricIndex === 0 && (
          <>
            {/* Inventory Cell */}
            <TableCell
              rowSpan={metricDefinitions.length}
              sx={{
                width: 220,
                minWidth: 220,
                maxWidth: 220,
                whiteSpace: "normal",
                wordBreak: "break-word",
                verticalAlign: "middle",
                position: "sticky",
                left: 0,
                backgroundColor: rowBackgroundColor,
                zIndex: 2,
                opacity: draggedKey === inventoryGrouping.key ? 0.4 : 1,
                boxShadow: dropPosition === MOVE_POSITION.BEFORE
                  ? "inset 0 3px 0 #1976d2"
                  : dropPosition === MOVE_POSITION.AFTER ? "inset 0 -3px 0 #1976d2" : undefined,
              }}
              onDragOver={(event) => {
                if (!acceptsDrop) return;
                event.preventDefault();
                const bounds = event.currentTarget.getBoundingClientRect();
                const position = event.clientY < bounds.top + bounds.height / 2 ? MOVE_POSITION.BEFORE : MOVE_POSITION.AFTER;
                if (dropPosition !== position) onDropTargetChange({ key: inventoryGrouping.key, position });
              }}
              onDrop={(event) => {
                if (!acceptsDrop || !draggedKey || !dropPosition) return;
                event.preventDefault();
                onMoveProduct(draggedKey, inventoryGrouping.key, dropPosition);
                onDraggedKeyChange(null);
                onDropTargetChange(null);
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  height: "100%",
                  textAlign: "center",
                  gap: 0.5,
                }}
              >
                {family && (
                  <Box sx={{ width: "100%", mb: 0.5, display: "flex", alignItems: "center", justifyContent: "center", gap: 0.5 }}>
                    <Tooltip title={isFamilyCollapsed ? `Show the ${family.members.length} products in this family` : "Fold the family to this one row"} arrow>
                      <IconButton size="small" sx={{ p: 0.25 }} onClick={() => familyControls.onToggleCollapsed(inventoryGrouping.key)}>
                        {isFamilyCollapsed ? <ChevronRightIcon sx={{ fontSize: 18 }} /> : <ExpandMoreIcon sx={{ fontSize: 18 }} />}
                      </IconButton>
                    </Tooltip>
                    <Box sx={{ fontWeight: 700, color: FAMILY_COLOR, fontSize: "0.75em", letterSpacing: 0.3 }}>ALL PACKS · {family.members.length} PRODUCTS</Box>
                    {collapseRowButton}
                  </Box>
                )}
                {isFamilyMember && (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <Box sx={{ fontSize: "0.7em", color: FAMILY_COLOR, fontWeight: 600 }}>↳ in family</Box>
                    {collapseRowButton}
                  </Box>
                )}

                {/* Pin + reorder controls (a member travels with its family) */}
                {!isFamilyMember && <Box sx={{ display: "flex", alignItems: "center", gap: 0.25, mb: 0.25 }}>
                  <Tooltip title="Drag to reorder" arrow>
                    <Box
                      draggable
                      onDragStart={(event) => {
                        event.dataTransfer.effectAllowed = "move";
                        event.dataTransfer.setData("text/plain", inventoryGrouping.key);
                        onDraggedKeyChange(inventoryGrouping.key);
                      }}
                      onDragEnd={() => {
                        onDraggedKeyChange(null);
                        onDropTargetChange(null);
                      }}
                      sx={{ display: "inline-flex", cursor: "grab", opacity: 0.5, "&:hover": { opacity: 1 } }}
                    >
                      <DragIndicatorIcon sx={{ fontSize: 18 }} />
                    </Box>
                  </Tooltip>
                  <Tooltip title="Move up" arrow>
                    <span>
                      <IconButton
                        size="small"
                        sx={{ p: 0.25 }}
                        disabled={!neighbors?.previousKey}
                        onClick={() => neighbors?.previousKey && onMoveProduct(inventoryGrouping.key, neighbors.previousKey, MOVE_POSITION.BEFORE)}
                      >
                        <KeyboardArrowUpIcon sx={{ fontSize: 18 }} />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title="Move down" arrow>
                    <span>
                      <IconButton
                        size="small"
                        sx={{ p: 0.25 }}
                        disabled={!neighbors?.nextKey}
                        onClick={() => neighbors?.nextKey && onMoveProduct(inventoryGrouping.key, neighbors.nextKey, MOVE_POSITION.AFTER)}
                      >
                        <KeyboardArrowDownIcon sx={{ fontSize: 18 }} />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title={isPinned ? "Unpin" : "Pin to the top of every page"} arrow>
                    <IconButton size="small" sx={{ p: 0.25 }} color={isPinned ? "warning" : "default"} onClick={() => onTogglePin(inventoryGrouping.key)}>
                      {isPinned ? <PushPinIcon sx={{ fontSize: 16 }} /> : <PushPinOutlinedIcon sx={{ fontSize: 16 }} />}
                    </IconButton>
                  </Tooltip>
                  {!family && collapseRowButton}
                </Box>}

                {whatIf && !family && (
                  <Box sx={{ width: "100%", mb: 0.5, p: 0.75, border: `1px solid ${WHAT_IF_COLOR}`, borderRadius: 1, backgroundColor: "background.paper", fontSize: "0.72em", lineHeight: 1.35 }}>
                    <Box sx={{ fontWeight: 700, color: WHAT_IF_COLOR, letterSpacing: 0.3 }}>WHAT-IF · NOT SAVED</Box>
                    <Box sx={{ opacity: 0.75 }}>Only you see these numbers</Box>
                    {whatIf.status === WHAT_IF_STATUS.ERROR && <Box sx={{ color: "error.main", mt: 0.5 }}>Could not recalculate: {whatIf.error}</Box>}
                    {whatIf.result && (
                      <Box sx={{ mt: 0.5, opacity: whatIf.status === WHAT_IF_STATUS.LOADING ? 0.5 : 1 }}>
                        <Box>Out of stock: {formatForecastDate(whatIf.result.baseline.stockOutDate)} → <strong>{formatForecastDate(whatIf.result.simulated.stockOutDate)}</strong></Box>
                        <Box>Order by: {formatForecastDate(whatIf.result.baseline.recommendedOrderDate)} → <strong>{formatForecastDate(whatIf.result.simulated.recommendedOrderDate)}</strong></Box>
                        <Box>Projected sales: {formatNumber(Math.round(whatIf.result.baseline.projectedSalesInPeriod))} → <strong>{formatNumber(Math.round(whatIf.result.simulated.projectedSalesInPeriod))}</strong></Box>
                      </Box>
                    )}
                    {whatIf.status === WHAT_IF_STATUS.LOADING && (
                      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 0.5, mt: 0.5 }}>
                        <CircularProgress size={10} /> Recalculating…
                      </Box>
                    )}
                    <Box sx={{ display: "flex", justifyContent: "center", gap: 0.5, mt: 0.5 }}>
                      <Button size="small" sx={{ fontSize: "1em", p: 0.25, minWidth: 0, textTransform: "none" }} onClick={() => whatIfControls.onDiscard(inventoryGrouping.inventory_id)}>
                        Discard
                      </Button>
                      <Tooltip
                        arrow
                        title={whatIfControls.unsavedInventoryIds.has(inventoryGrouping.inventory_id)
                          ? "Make this part of the team's real forecast (you'll be asked to confirm)"
                          : "A trial daily rate can't be saved. To keep it, enter the months' sales as manual overrides."}
                      >
                        <span>
                          <Button
                            size="small"
                            variant="contained"
                            sx={{ fontSize: "1em", p: "2px 8px", minWidth: 0, textTransform: "none", backgroundColor: WHAT_IF_COLOR, "&:hover": { backgroundColor: WHAT_IF_COLOR } }}
                            disabled={!whatIfControls.unsavedInventoryIds.has(inventoryGrouping.inventory_id)}
                            onClick={() => whatIfControls.onSave(inventoryGrouping.inventory_id)}
                          >
                            Save…
                          </Button>
                        </span>
                      </Tooltip>
                    </Box>
                  </Box>
                )}

                {inventoryGrouping.inventory_name && (
                  <Box sx={{ fontWeight: 500, lineHeight: 1.2 }}>
                    {inventoryGrouping.inventory_name}
                  </Box>
                )}
                {!family && (
                  <>
                    <Box sx={{ fontSize: "0.85em", opacity: 0.7, lineHeight: 1.2 }}>
                      {inventoryGrouping.inventory_id}
                    </Box>

                    {/* Danican product number */}
                    <Box sx={{ fontSize: "0.8em", lineHeight: 1.2, fontWeight: 500, opacity: inventoryGrouping.manufacturerSku ? 0.85 : 0.45 }}>
                      {inventoryGrouping.manufacturerSku || "No Danican #"}
                    </Box>
                  </>
                )}

                {/* Manufacturer */}
                {inventoryGrouping.manufacturer && (
                  <Box sx={{ fontSize: "0.75em", opacity: 0.6, mt: 0.5 }}>
                    {inventoryGrouping.manufacturer}
                  </Box>
                )}

                {/* Committed / On-Hand inventory tooltip */}
                {(inventoryGrouping.committedQuantity != null && inventoryGrouping.committedQuantity > 0) && (
                  <Tooltip
                    title={`Fulfillable: ${formatNumber(Math.round(inventoryGrouping.onhandQuantity ?? 0) - Math.round(inventoryGrouping.committedQuantity ?? 0))} | Committed: ${formatNumber(Math.round(inventoryGrouping.committedQuantity))} | On-Hand: ${formatNumber(Math.round(inventoryGrouping.onhandQuantity ?? 0))}`}
                    arrow
                  >
                    <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.25, fontSize: "0.7em", opacity: 0.6, mt: 0.25, cursor: "help" }}>
                      <InfoOutlinedIcon sx={{ fontSize: "1em" }} />
                      <span>{formatNumber(Math.round(inventoryGrouping.committedQuantity))} committed</span>
                    </Box>
                  </Tooltip>
                )}

                {/* SKUs */}
                {inventoryGrouping.skus && inventoryGrouping.skus.length > 0 && (
                  inventoryGrouping.skus.length <= 3 ? (
                    <Box sx={{ fontSize: "0.7em", opacity: 0.5, mt: 0.25 }}>
                      {inventoryGrouping.skus.join(", ")}
                    </Box>
                  ) : (
                    <Box
                      sx={{
                        fontSize: "0.7em",
                        opacity: 0.5,
                        mt: 0.25,
                        cursor: "pointer",
                        "&:hover": { textDecoration: "underline" },
                      }}
                      onClick={() => onOpenSkuModal(
                        inventoryGrouping.skus || [],
                        inventoryGrouping.inventory_name || inventoryGrouping.inventory_id,
                      )}
                    >
                      {inventoryGrouping.skus.slice(0, 2).join(", ")} <span style={{ color: "#1976d2" }}>+{inventoryGrouping.skus.length - 2} more</span>
                    </Box>
                  )
                )}

                {family && (() => {
                  const { substitution, members } = family;
                  const outMembers = members.filter(member => member.stockOutDate);
                  const firstOut = outMembers.length > 0 ? outMembers.reduce((earliest, member) => (member.stockOutDate! < earliest.stockOutDate! ? member : earliest)) : null;
                  const isImbalanced = firstOut != null && (family.stockOutSpreadDays == null || family.stockOutSpreadDays > PACK_IMBALANCE_DAYS);
                  const packName = (inventoryId: string) => members.find(member => member.inventoryId === inventoryId)?.inventoryName ?? inventoryId;
                  return (
                    <Box sx={{ width: "100%", mt: 0.75, p: 0.75, border: `1px solid ${FAMILY_COLOR}`, borderRadius: 1, backgroundColor: "background.paper", fontSize: "0.72em", lineHeight: 1.4, textAlign: "left" }}>
                      <Box sx={{ opacity: 0.75, mb: 0.25 }}>Counted in {family.unitLabel.replace(/^units of /, "units of the ")}</Box>
                      {members.map(member => (
                        <Box key={member.key}>
                          {member.qty > 1 ? `${member.qty}-pack` : "Single"}: {formatNumber(Math.round(member.currentInventory))} in stock, {member.dailyRunRate.toFixed(1)}/day, out {formatForecastDate(member.stockOutDate)}
                        </Box>
                      ))}
                      {isImbalanced && firstOut && (
                        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 0.5, mt: 0.5, color: "warning.dark", fontWeight: 600 }}>
                          <WarningAmberIcon sx={{ fontSize: 14, mt: "1px" }} />
                          <span>
                            Pack mix imbalance: the {firstOut.qty > 1 ? `${firstOut.qty}-pack` : "single"} runs out {formatForecastDate(firstOut.stockOutDate)}
                            {family.stockOutSpreadDays != null ? `, ${Math.round(family.stockOutSpreadDays / 7)} weeks before the rest` : " while the rest last beyond the range"} — order more of it.
                          </span>
                        </Box>
                      )}
                      {substitution.substitutedOrders > 0 && (
                        <Box sx={{ mt: 0.5, color: "error.dark" }}>
                          Once singles are out ({formatForecastDate(substitution.baseStockOutDate)}), single orders ship a whole pack: about {formatNumber(substitution.substitutedOrders)} orders and {formatNumber(substitution.giveawayUnits)} units given away
                          {substitution.packsStockOutDate ? `; every pack gone by ${formatForecastDate(substitution.packsStockOutDate)}` : " in range"}.
                        </Box>
                      )}
                      <Box sx={{ mt: 0.5, opacity: 0.8 }}>
                        Last {familyControls.substitutionDays} days: {familySubstitutions
                          ? `${formatNumber(familySubstitutions.orders)} orders shipped a different pack than usual (${formatNumber(familySubstitutions.giveawayUnits)} units given away)`
                          : "no orders shipped a different pack than usual"}
                      </Box>
                      {members.length > 0 && <Box sx={{ mt: 0.5, opacity: 0.6 }}>What-ifs on the products below don't change this row yet.</Box>}
                    </Box>
                  );
                })()}

                {/* Current Stock & Inventory Value */}
                {(currentStock != null || inventoryValue != null) && (
                  <Box sx={{ display: "flex", gap: 1.5, mt: 0.75, fontSize: "0.7em" }}>
                    {currentStock != null && (
                      <Box>
                        <Box sx={{ opacity: 0.5, lineHeight: 1.1 }}>Stock</Box>
                        <Box sx={{ fontWeight: 600, lineHeight: 1.2 }}>{formatNumber(Math.round(currentStock))}</Box>
                      </Box>
                    )}
                    {inventoryValue != null && (
                      <Box>
                        <Box sx={{ opacity: 0.5, lineHeight: 1.1 }}>Value</Box>
                        <Box sx={{ fontWeight: 600, lineHeight: 1.2 }}>${formatNumber(inventoryValue)}</Box>
                      </Box>
                    )}
                  </Box>
                )}
              </Box>
            </TableCell>

            {/* Country Cell */}
            <TableCell
              rowSpan={metricDefinitions.length}
              sx={{
                verticalAlign: "middle",
                position: "sticky",
                left: 220,
                backgroundColor: rowBackgroundColor,
                zIndex: 2,
                width: 100,
                minWidth: 100,
                maxWidth: 100,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  height: "100%",
                  textAlign: "center",
                }}
              >
                {inventoryGrouping.country}
              </Box>
            </TableCell>
          </>
        )}

        {/* Metric Label Cell */}
        <TableCell
          sx={{
            position: "sticky",
            left: 320,
            backgroundColor: rowBackgroundColor,
            zIndex: 2,
            width: 220,
            minWidth: 220,
            maxWidth: 220,
            boxShadow: "2px 0 4px rgb(200, 200, 200)",
            fontWeight: metricDefinition.key === "endingInventory" ? 600 : undefined,
          }}
        >
          {metricDefinition.key === "soldQuantity" ? (
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%" }}>
              {metricDefinition.label}
              {!family && <Box sx={{ display: "flex", alignItems: "center", gap: 0.25 }}>
                <Tooltip title="View projection details">
                  <IconButton
                    size="small"
                    onClick={() => onOpenProjectionDetail(inventoryGrouping)}
                    sx={{ p: 0.15 }}
                  >
                    <InfoOutlinedIcon sx={{ fontSize: 12 }} />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Edit sales modifiers for this item">
                  <IconButton
                    size="small"
                    onClick={() => onSetEditingInventoryModifiers(
                      editingInventoryModifiers === inventoryGrouping.key ? null : inventoryGrouping.key
                    )}
                    sx={{
                      p: 0.15,
                      backgroundColor: editingInventoryModifiers === inventoryGrouping.key ? "primary.light" : undefined,
                      color: salesModifiers.byInventory[inventoryGrouping.inventory_id] ? "primary.main" : undefined,
                    }}
                  >
                    <EditIcon sx={{ fontSize: 12 }} />
                  </IconButton>
                </Tooltip>
              </Box>}
            </Box>
          ) : metricDefinition.key === "confirmedInventoryAdded" ? (
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%" }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                {metricDefinition.label}
                {confirmedPOs.length > 0 && (
                  <Typography variant="caption" sx={{ color: "success.main", fontSize: "0.65em" }}>
                    ({confirmedPOs.length})
                  </Typography>
                )}
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.25 }}>
                {!family && pastDueUnreceivedPOs.length > 0 && (
                  <Tooltip title={`${pastDueUnreceivedPOs.length} past-due PO(s) unreceived — click to manage`}>
                    <IconButton
                      size="small"
                      onClick={() => onOpenIncomingPODialog(inventoryGrouping.inventory_id)}
                      sx={{ p: 0.15, color: "warning.main" }}
                    >
                      <CheckCircleOutlineIcon sx={{ fontSize: 13 }} />
                    </IconButton>
                  </Tooltip>
                )}
                {!family && <Tooltip title="Manage incoming POs">
                  <IconButton
                    size="small"
                    onClick={() => onOpenIncomingPODialog(inventoryGrouping.inventory_id)}
                    sx={{
                      p: 0.15,
                      color: confirmedPOs.length > 0 ? "success.main" : undefined,
                    }}
                  >
                    <AddIcon sx={{ fontSize: 13 }} />
                  </IconButton>
                </Tooltip>}
              </Box>
            </Box>
          ) : metricDefinition.key === "draftInventoryAdded" ? (
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%" }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                {metricDefinition.label}
                {draftPOs.length > 0 && (
                  <Typography variant="caption" sx={{ color: "text.secondary", fontSize: "0.65em" }}>
                    ({draftPOs.length})
                  </Typography>
                )}
              </Box>
              {!family && <Tooltip title="Manage POs">
                <IconButton
                  size="small"
                  onClick={() => onOpenIncomingPODialog(inventoryGrouping.inventory_id)}
                  sx={{ p: 0.15 }}
                >
                  <AddIcon sx={{ fontSize: 13 }} />
                </IconButton>
              </Tooltip>}
            </Box>
          ) : (
            metricDefinition.label
          )}
        </TableCell>

        {/* Period Value Cells */}
        {periodColumns.map((periodColumn) => {
          const periodMetrics = valuesForGrouping.get(periodColumn.key);
          const metricValue = periodMetrics?.[metricDefinition.key] ?? null;
          const isFuture = periodColumn.periodStart > today;
          const endingInventory = periodMetrics?.endingInventory ?? null;
          const isNegative = endingInventory != null && endingInventory < 0;

          // Determine tooltip for forecasted cells
          let tooltipText: React.ReactNode = "";
          let tooltipFollowsCursor = false;
          if (metricDefinition.key === "soldQuantity" && periodMetrics?.soldBreakdown) {
            tooltipText = periodMetrics.soldBreakdown;
          } else if (metricDefinition.key === "endingInventory" && periodMetrics?.endingBreakdown) {
            tooltipText = periodMetrics.endingBreakdown;
          } else if (metricDefinition.key === "miscAdjustment" && metricValue != null && !isFuture) {
            const stowed = periodMetrics?.stowedPOBreakdown ?? [];
            tooltipFollowsCursor = true;
            tooltipText = (
              <Box component="span" sx={{ display: "block", maxWidth: 260 }}>
                <Box component="span" sx={{ display: "block" }}>
                  ShipBob stock change not explained by sales
                  (WRO receipts, returns, corrections).
                </Box>
                {stowed.length > 0 && (
                  <Box component="span" sx={{ display: "block", mt: 0.5 }}>
                    Stowed from WROs:
                  </Box>
                )}
                {stowed.map((contribution: POContribution) => (
                  <Box component="span" key={contribution.poNumber} sx={{ display: "block" }}>
                    {contribution.poNumber}
                    {contribution.wroIds?.length ? ` · WRO ${contribution.wroIds.join(", ")}` : ""}: +{formatNumber(contribution.quantity)}
                  </Box>
                ))}
              </Box>
            );
          } else if (
            metricDefinition.key === "confirmedInventoryAdded" ||
            metricDefinition.key === "draftInventoryAdded"
          ) {
            const poBreakdown = metricDefinition.key === "confirmedInventoryAdded"
              ? periodMetrics?.confirmedPOBreakdown
              : periodMetrics?.draftPOBreakdown;
            if (poBreakdown && poBreakdown.length > 0) {
              tooltipFollowsCursor = true;
              const anyProjected = poBreakdown.some((c: POContribution) => c.projected);
              tooltipText = (
                <Box component="span" sx={{ display: "block", whiteSpace: "nowrap" }}>
                  {poBreakdown.map((contribution: POContribution) => (
                    <Box component="span" key={contribution.poNumber} sx={{ display: "block" }}>
                      {contribution.poNumber}: {formatNumber(contribution.quantity)}
                      {contribution.projected ? " · projected" : ""}
                    </Box>
                  ))}
                  {anyProjected && (
                    <Box component="span" sx={{ display: "block", mt: 0.5, whiteSpace: "normal", maxWidth: 260 }}>
                      "Projected" means the PO is past its arrive-by date and has not been
                      received, so it is expected here based on where ShipBob last reported
                      its WRO, not on its own date.
                    </Box>
                  )}
                </Box>
              );
            }
          }

          let backgroundColor = rowBackgroundColor;
          if (isFuture) {
            backgroundColor = "rgb(240, 248, 255)";
          }
          if (isNegative) {
            backgroundColor = "rgb(255, 240, 240)";
          }

          const cellContent = metricDefinition.key === "soldQuantity" ? (
            <span>{formatNumber(metricValue)}</span>
          ) : (
            formatNumber(metricValue)
          );

          return (
            <TableCell
              key={`${inventoryGrouping.key}::${metricDefinition.key}::${periodColumn.key}`}
              align="right"
              onClick={metricDefinition.key === "soldQuantity" && canOpenProjectionDetail ? () => onOpenProjectionDetail(inventoryGrouping) : undefined}
              sx={{
                backgroundColor: backgroundColor,
                fontWeight: metricDefinition.key === "endingInventory" ? 600 : undefined,
                ...(metricDefinition.key === "soldQuantity" && canOpenProjectionDetail && {
                  cursor: "pointer",
                  "&:hover span": {
                    textDecoration: "underline",
                  },
                }),
              }}
            >
              {tooltipText ? (
                <Tooltip title={tooltipText} arrow placement="top" followCursor={tooltipFollowsCursor}>
                  <Box component="span" sx={{ borderBottom: "1px dotted rgba(0,0,0,0.3)", cursor: "help" }}>
                    {cellContent}
                  </Box>
                </Tooltip>
              ) : (
                cellContent
              )}
            </TableCell>
          );
        })}
      </TableRow>
    );
  });

  // Inventory-specific modifiers row
  if (!family && (editingInventoryModifiers === inventoryGrouping.key || hasInventorySpecificModifiers)) {
    const shownMonthsForInventory = new Set<string>();

    rows.push(
      <TableRow key={`${inventoryGrouping.key}::modifiers`} sx={{ backgroundColor: "rgb(248, 250, 255)" }}>
        <TableCell
          colSpan={3}
          sx={{
            position: "sticky",
            left: 0,
            backgroundColor: "rgb(248, 250, 255)",
            zIndex: 2,
            boxShadow: "2px 0 4px rgb(200, 200, 200)",
            verticalAlign: "middle",
            p: 1,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Typography variant="caption" sx={{ fontWeight: 600 }}>
              Item Multiplier
            </Typography>
            <Button
              size="small"
              color="error"
              variant="text"
              onClick={() => onClearInventoryModifiers(inventoryGrouping.inventory_id)}
              disabled={!salesModifiers.byInventory[inventoryGrouping.inventory_id]}
              sx={{ minWidth: "auto", fontSize: "0.7em", p: 0.5 }}
            >
              Clear
            </Button>
          </Box>
        </TableCell>
        {periodColumns.map((periodColumn, colIndex) => {
          const hasFutureDates = periodColumn.periodEnd >= today;
          const monthKey = moment(periodColumn.periodStart).format("YYYY-MM");

          if (!hasFutureDates) {
            return (
              <TableCell
                key={periodColumn.key}
                sx={{ backgroundColor: "rgb(248, 250, 255)", opacity: 0.5 }}
              />
            );
          }

          const alreadyShown = shownMonthsForInventory.has(monthKey);
          shownMonthsForInventory.add(monthKey);

          let colspan = 1;
          if (!alreadyShown) {
            for (let i = colIndex + 1; i < periodColumns.length; i++) {
              const nextMonth = moment(periodColumns[i].periodStart).format("YYYY-MM");
              if (nextMonth === monthKey) {
                colspan++;
              } else {
                break;
              }
            }
          }

          if (alreadyShown) {
            return null;
          }

          const inventoryModifier = salesModifiers.byInventory[inventoryGrouping.inventory_id]?.[monthKey];
          const globalModifier = salesModifiers.global[monthKey];
          const hasInventoryOverride = inventoryModifier !== undefined;
          const inputWidth = bucketGranularity === "day" ? 60 :
                             bucketGranularity === "week" ? 70 : 80;

          return (
            <TableCell
              key={periodColumn.key}
              colSpan={colspan}
              align="center"
              sx={{ backgroundColor: "rgb(248, 250, 255)", p: 0.5 }}
            >
              <Tooltip title={`Multiplier for ${moment(monthKey).format("MMM YYYY")} (inherits ${globalModifier ?? 1.0} from global)`}>
                <TextField
                  size="small"
                  type="number"
                  value={inventoryModifier ?? ""}
                  placeholder={globalModifier !== undefined ? `${globalModifier}` : "1.0"}
                  onChange={(e) => {
                    const value = e.target.value === "" ? null : parseFloat(e.target.value);
                    onUpdateInventoryModifier(inventoryGrouping.inventory_id, monthKey, value);
                  }}
                  inputProps={{
                    step: 0.1,
                    min: 0,
                    style: {
                      textAlign: "center",
                      padding: "4px 4px",
                      fontSize: "0.75rem",
                    },
                  }}
                  sx={{
                    width: inputWidth,
                    "& .MuiOutlinedInput-root": {
                      backgroundColor: hasInventoryOverride ? "rgb(255, 255, 230)" : "white",
                    },
                  }}
                />
              </Tooltip>
            </TableCell>
          );
        }).filter(Boolean)}
      </TableRow>
    );
  }

  // Spacer row
  if (!isLastGrouping) {
    const spacerBaseStyle = {
      height: "20px",
      padding: 0,
      backgroundColor: "white",
      borderTop: "none",
      borderLeft: "none",
      borderRight: "none",
      borderBottom: "1px solid",
      borderBottomColor: "divider",
    };

    const stickyColumns = [
      { left: 0, width: 220, boxShadow: undefined },
      { left: 220, width: 100, boxShadow: undefined },
      { left: 320, width: 220, boxShadow: "2px 0 4px rgb(200, 200, 200)" },
    ];

    rows.push(
      <TableRow key={`${inventoryGrouping.key}::spacer`}>
        {stickyColumns.map((config, idx) => (
          <TableCell
            key={idx}
            sx={{
              ...spacerBaseStyle,
              position: "sticky",
              left: config.left,
              width: config.width,
              minWidth: config.width,
              maxWidth: config.width,
              zIndex: 2,
              boxShadow: config.boxShadow,
            }}
          />
        ))}
        {periodColumns.map((periodColumn) => (
          <TableCell key={periodColumn.key} sx={spacerBaseStyle} />
        ))}
      </TableRow>
    );
  }

  return <>{rows}</>;
}
